import type { FC } from 'react';
import React from 'react';
import {
  Flex,
  Body,
  Box,
  Pill,
  DecorationLine,
  Col,
  FlexRow,
  Glyph,
} from '@edreamsodigeo/prisma-design-system';
import { useTranslation } from '@edreamsodigeo/frontend-core';

import LeftContentWrapper from './LeftContentWrapper';
import type { SectionFragment } from '@/generated/graphql';
import { CabinClass, TransportType } from '@/generated/graphql';
import QualityPill from '@/components/FlightSummary/AirlineQualityTag/QualityPill/QualityPill';
import type { QualityPillType } from '@/components/FlightSummary/AirlineQualityTag/QualityPill/config/types';
import useItineraryFromUrl from '@/clientState/hooks/useItineraryFromUrl';
import { CabinClassComponent } from '@/components/FlightSummary/CabinClass/CabinClassComponent';
import { VehicleModel } from '@/components/FlightSummary/VehicleModel/VehicleModel';
import { DurationWithIcon } from '@/components/Duration/DurationWithIcon';
import CarrierLine from './CarrierLine';

interface SectionTripFlightMapItineraryProps {
  carrier: SectionFragment['carrier'];
  flightCode: SectionFragment['flightCode'];
  duration: SectionFragment['duration'];
  cabinClass: SectionFragment['cabinClass'];
  isTrain: boolean;
  operatingCarrier: SectionFragment['operatingCarrier'];
  commercialBrand: SectionFragment['commercialBrand'];
  qualityPillType?: QualityPillType;
  vehicleModel?: string | null;
}
interface FlightInfoLineProps {
  cabinClass: CabinClass;
  duration: SectionFragment['duration'];
  isTrain: boolean;
  vehicleModel?: string | null;
}

const InfoTripPill: FC = ({ children }) => {
  return (
    <Flex mb="1x_small">
      <Pill
        type="neutral"
        theme="soft"
        rounded
        sx={{
          py: `1px`,
        }}
      >
        {children}
      </Pill>
    </Flex>
  );
};

const FlightInfoLine: FC<FlightInfoLineProps> = ({
  cabinClass,
  duration,
  isTrain,
  vehicleModel,
}) => {
  const { t } = useTranslation();
  const color = 'neutral.80';
  const { qualityTagType } = useItineraryFromUrl();

  return (
    <FlexRow flexWrap="wrap" gap="1" mb="minus_1x_small">
      <Col>
        <InfoTripPill>
          <DurationWithIcon
            duration={duration || 0}
            color={color}
            weight="normal"
            type="base"
          />
        </InfoTripPill>
      </Col>
      {isTrain ? (
        <Col>
          <InfoTripPill>
            <FlexRow flexDirection="row" alignItems="center" gap="0">
              <Col>
                <Box verticalAlign="middle">
                  <Glyph
                    size="small"
                    color={color}
                    category="TravelProducts"
                    name="Train"
                  />
                </Box>
              </Col>
              <Col>
                <Body color={color} type="base">
                  {t('flightSummary.itinerary.train')}
                </Body>
              </Col>
            </FlexRow>
          </InfoTripPill>
        </Col>
      ) : null}
      <Col>
        <InfoTripPill>
          <FlexRow flexDirection="row" alignItems="center" gap="0">
            <Col>
              <Box verticalAlign="middle">
                <Glyph
                  size="medium"
                  color={color}
                  sx={{ mb: 'minus_1x_small' }}
                  category="TravelProducts"
                  name="AirplaneTicket"
                />
              </Box>
            </Col>
            <Col>
              <CabinClassComponent
                type={cabinClass}
                color={color}
                weight="normal"
                fontSize="base"
              />
            </Col>
          </FlexRow>
        </InfoTripPill>
      </Col>
      <Col>
        {!isTrain && vehicleModel && (
          <InfoTripPill>
            <VehicleModel
              transportType={TransportType.Plane}
              model={vehicleModel}
            />
          </InfoTripPill>
        )}
      </Col>
      {qualityTagType && (
        <Col>
          <QualityPill type={qualityTagType} />
        </Col>
      )}
    </FlexRow>
  );
};

const SectionTripFlightMapItinerary: FC<SectionTripFlightMapItineraryProps> = ({
  carrier,
  flightCode,
  duration,
  cabinClass,
  isTrain,
  operatingCarrier,
  commercialBrand,
  vehicleModel,
}) => {
  const { t } = useTranslation();

  return (
    <Flex>
      <LeftContentWrapper />
      <DecorationLine lineType="solid" decorationType="none" />
      <Flex flexDirection="column" mb="big">
        <CarrierLine
          carrierId={carrier.id}
          carrierName={carrier.name}
          commercialBrand={commercialBrand}
          flightCode={flightCode}
        />
        {operatingCarrier && operatingCarrier.id !== carrier.id && (
          <Box mb="2x_small" ml="2x_small" mt="minus_1x_small">
            <Body type="small" weight="medium" color="neutral.80">
              {t('flightSummary.itinerary.section.operatingBy')}:{' '}
              {operatingCarrier.name}
            </Body>
          </Box>
        )}
        <FlightInfoLine
          cabinClass={cabinClass ?? CabinClass.EconomicDiscounted}
          duration={duration}
          isTrain={isTrain}
          vehicleModel={vehicleModel}
        />
      </Flex>
    </Flex>
  );
};

export default SectionTripFlightMapItinerary;
